/**
 * Motion Presets — Reusable Framer Motion configs for consistent "tactile" feel.
 *
 * These presets give every interactive element a physical, app-like response.
 * All presets respect useMotionBudget — consumers should check `shouldAnimate`
 * before applying hover/tap effects on mobile.
 *
 * Usage:
 *   import { tapSpring, hoverLift, cardInteraction } from './motionPresets'
 *   <motion.button {...tapSpring}>Click me</motion.button>
 */

import type { Variants } from 'framer-motion'

// ── Tap / Press Presets ─────────────────────────────────────────────

/** Subtle press-down on tap — use on all buttons */
export const tapSpring = {
  whileTap: { scale: 0.97 },
  transition: { type: 'spring' as const, stiffness: 400, damping: 25 },
} as const

/** Stronger press for primary actions */
export const tapBounce = {
  whileTap: { scale: 0.95 },
  transition: { type: 'spring' as const, stiffness: 500, damping: 20 },
} as const

// ── Hover Presets ───────────────────────────────────────────────────

/** Subtle lift on hover — use on clickable cards */
export const hoverLift = {
  whileHover: {
    y: -3,
    transition: { type: 'spring' as const, stiffness: 400, damping: 25 },
  },
} as const

/** Scale up slightly on hover — use on icon buttons */
export const hoverGrow = {
  whileHover: {
    scale: 1.05,
    transition: { type: 'spring' as const, stiffness: 400, damping: 25 },
  },
} as const

// ── Combined Presets ────────────────────────────────────────────────

/** Full card interaction: lift on hover + press on tap */
export const cardInteraction = {
  whileHover: {
    y: -3,
    boxShadow:
      '0 8px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 10px -5px rgba(0, 0, 0, 0.04)',
  },
  whileTap: { scale: 0.98, y: 0 },
  transition: { type: 'spring' as const, stiffness: 400, damping: 25 },
} as const

/** Nav item: slide right on hover + press on tap */
export const navItemInteraction = {
  whileHover: { x: 4 },
  whileTap: { scale: 0.98 },
  transition: { type: 'spring' as const, stiffness: 400, damping: 25 },
} as const

// ── Enter/Exit Variants ─────────────────────────────────────────────

/** Fade + slide up — default page/section entrance */
export const fadeSlideUp: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 300, damping: 30 },
  },
  exit: { opacity: 0, y: -8, transition: { duration: 0.15 } },
}

/** Scale + fade — for modals and overlays */
export const scaleFade: Variants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { type: 'spring', stiffness: 400, damping: 30 },
  },
  exit: { opacity: 0, scale: 0.95, transition: { duration: 0.15 } },
}

/** Pop in — for badges, notifications, floating elements */
export const popIn: Variants = {
  hidden: { opacity: 0, scale: 0.6 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { type: 'spring', stiffness: 500, damping: 25 },
  },
  exit: { opacity: 0, scale: 0.8, transition: { duration: 0.12 } },
}

/** Slide from right — for drawers, panels */
export const slideFromRight: Variants = {
  hidden: { x: '100%', opacity: 0 },
  visible: {
    x: 0,
    opacity: 1,
    transition: { type: 'spring', stiffness: 300, damping: 30 },
  },
  exit: { x: '100%', opacity: 0, transition: { duration: 0.2 } },
}

// ── Micro-animation Helpers ─────────────────────────────────────────

/** Gentle breathing/pulse — for idle states (Djay, streak flame) */
export const breathe: Variants = {
  idle: {
    scale: [1, 1.03, 1],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
}

/** Wiggle — for attention-grabbing moments */
export const wiggle: Variants = {
  idle: { rotate: 0 },
  wiggle: {
    rotate: [0, -3, 3, -2, 2, 0],
    transition: { duration: 0.5, ease: 'easeInOut' },
  },
}

/** Float up and fade — for XP gain text, notifications */
export const floatUpAndFade: Variants = {
  hidden: { opacity: 0, y: 0, scale: 0.8 },
  visible: {
    opacity: [0, 1, 1, 0],
    y: -40,
    scale: 1,
    transition: { duration: 1.2, ease: 'easeOut' },
  },
}

/** Glow pulse — for XP bars, active indicators */
export const glowPulse = {
  animate: {
    boxShadow: [
      '0 0 0 0 rgba(249, 115, 22, 0)',
      '0 0 8px 2px rgba(249, 115, 22, 0.3)',
      '0 0 0 0 rgba(249, 115, 22, 0)',
    ],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
} as const
